import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';
import { Patient, PatientStatus, PatientPriority, PaymentMode } from '@/types';
import { supabase } from '@/lib/supabase';
import { getCurrentHospitalId, requireHospitalIdForWrite } from '@/lib/hospitalContext';
import { generatePatientIdSync } from '@/lib/utils/patientId';
import { trackChange } from '@/lib/utils/sync-tracker';
import { hospitalCacheKey } from '@/lib/utils/tenantCache';

function isUniqueViolation(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  return (
    error.code === '23505' ||
    /duplicate key|unique constraint/i.test(error.message ?? '')
  );
}

function formatSupabaseError(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return 'Failed to create patient';
  }
  const e = error as { message?: string; details?: string; hint?: string; code?: string };
  const parts = [e.message, e.details, e.hint].filter(Boolean);
  return parts.length > 0 ? parts.join(' — ') : 'Failed to create patient';
}

function rpcAgeForInsert(age: unknown): number {
  if (typeof age === 'number' && !Number.isNaN(age)) return age;
  const parsed = parseInt(String(age ?? '').replace(/\D/g, ''), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

async function getHospitalNameForPatientId(hospitalId: string): Promise<string | undefined> {
  try {
    const { useHospital } = await import('../hooks/useHospital');
    const name = useHospital.getState().currentHospital?.name;
    if (name) return name;
  } catch {
    /* use DB fallback */
  }
  const { data } = await supabase
    .from('hospitals')
    .select('name')
    .eq('id', hospitalId)
    .maybeSingle();
  return data?.name ?? undefined;
}

async function insertPatientViaRpc(
  hospitalId: string,
  hospitalName: string | undefined,
  insertPayload: Record<string, unknown>
): Promise<Record<string, unknown> | null> {
  const { getPatientIdPrefix } = await import('@/lib/utils/patientId');
  const prefix = getPatientIdPrefix(hospitalName);

  const { data, error } = await supabase.rpc('insert_queue_patient', {
    p_id_prefix: prefix,
    p_hospital_id: hospitalId,
    p_name: insertPayload.name,
    p_age: rpcAgeForInsert(insertPayload.age),
    p_gender: insertPayload.gender,
    p_contact_number: insertPayload.contact_number,
    p_reason: insertPayload.reason,
    p_priority: insertPayload.priority ?? 'normal',
    p_referral_doctor: insertPayload.referral_doctor ?? null,
    p_location: insertPayload.location ?? null,
    p_doctor_id: insertPayload.doctor_id ?? null,
    p_registered_at: insertPayload.registered_at,
  });

  if (error) {
    const missingFn =
      error.code === 'PGRST202' || /Could not find the function/i.test(error.message ?? '');
    console.warn(
      missingFn
        ? 'insert_queue_patient is not installed — using direct insert. Run database/fix_patient_global_id.sql in Supabase.'
        : 'insert_queue_patient failed — using direct insert:',
      error.message,
      error.details ?? '',
      error.hint ?? '',
      error.code ?? ''
    );
    return null;
  }

  if (data && typeof data === 'object') {
    return data as Record<string, unknown>;
  }
  return null;
}

function pickLaterPatientId(a: string, b: string): string {
  const seqA = parseInt(a.split('-')[1] ?? '0', 10) || 0;
  const seqB = parseInt(b.split('-')[1] ?? '0', 10) || 0;
  return seqA >= seqB ? a : b;
}

async function resolveNextPatientId(
  hospitalName: string | undefined,
  localPatients: Patient[],
  extraIds: string[] = []
): Promise<string> {
  const { getPatientIdPrefix } = await import('@/lib/utils/patientId');
  const prefix = getPatientIdPrefix(hospitalName);

  const { data: rpcId, error: rpcError } = await supabase.rpc('next_queue_patient_id', {
    p_id_prefix: prefix,
  });

  const { data: rows } = await supabase
    .from('patients')
    .select('id')
    .like('id', `${prefix}-%`);

  const dbIds = (rows || []).map((r) => r.id as string);
  const fromClient = generatePatientIdSync(localPatients, hospitalName, [
    ...dbIds,
    ...extraIds,
  ]);

  if (!rpcError && typeof rpcId === 'string' && rpcId.length > 0) {
    return pickLaterPatientId(rpcId, fromClient);
  }

  if (rpcError && rpcError.code !== 'PGRST202') {
    console.warn('next_queue_patient_id RPC unavailable:', rpcError.message);
  }

  return fromClient;
}

function mapRowToPatient(row: Record<string, unknown>): Patient {
  const age = row.age;
  return {
    id: row.id as string,
    name: row.name as string,
    age:
      typeof age === 'number'
        ? `${age} years`
        : (age as string) ?? '',
    gender: row.gender as Patient['gender'],
    contactNumber: row.contact_number as string,
    priority: row.priority as Patient['priority'],
    reason: row.reason as string,
    referralDoctor: row.referral_doctor as string | undefined,
    location: row.location as string | undefined,
    doctorId: row.doctor_id as string | undefined,
    registeredAt: row.registered_at as string,
    status: row.status as Patient['status'],
    amountPaid: (row.amount_paid as number) ?? 0,
    paymentMode: (row.payment_mode as Patient['paymentMode']) ?? 'cash',
    paymentHistory: (row.payment_history as Patient['paymentHistory']) ?? [],
    services: (row.services as Patient['services']) ?? [],
    followUpDate: (row.follow_up_date as string | null) ?? null,
    followUpStatus: (row.follow_up_status as Patient['followUpStatus']) || 'scheduled',
    notes: (row.notes as string) ?? '',
    appointmentTime: row.appointment_time as string | undefined,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
    completedAt: row.completed_at as string | undefined,
    completedBy: row.completed_by as string | undefined,
  };
}

interface PatientsState {
  patients: Patient[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: number;
  pendingUpdates: Set<string>; // Track optimistic updates
  fetchPatients: () => Promise<void>;
  addPatient: (patient: {
    name: string;
    age: string | number;
    gender: 'male' | 'female' | 'other';
    contactNumber: string;
    priority?: PatientPriority;
    reason: string;
    referralDoctor?: string;
    location?: string;
    doctorId?: string;
    registeredAt?: string;
  }) => Promise<Patient>;
  updatePatientStatus: (patientId: string, status: PatientStatus, completedBy?: string) => Promise<void>;
  updatePatientDoctor: (patientId: string, doctorId: string) => Promise<void>;
  addPatientServices: (patientId: string, services: any[]) => Promise<void>;
  removePatientService: (patientId: string, serviceId: string) => Promise<void>;
  updatePatientPayment: (patientId: string, amount: number, paymentMode: PaymentMode) => Promise<void>;
  updatePatientFollowUp: (patientId: string, followUpDate: Date, notes?: string) => Promise<void>;
  resetPatientSession: (patientId: string) => Promise<void>;
  removePatient: (id: string) => Promise<void>;
  getPatientsByStatus: (status: PatientStatus) => Patient[];
  getPatientsByDoctor: (doctorId: string) => Patient[];
  updatePatient: (patientId: string, updates: Partial<Patient>) => Promise<void>;
  resetStore: () => void;
}

async function savePatientsCache(patients: Patient[]) {
  const hospitalId = await getCurrentHospitalId();
  if (!hospitalId) return;
  try {
    localStorage.setItem(
      hospitalCacheKey('patients-cache', hospitalId),
      JSON.stringify(patients)
    );
  } catch {}
}

export const usePatientsStore = create<PatientsState>()((set, get) => ({
  patients: [],
  isLoading: false,
  error: null,
  lastUpdated: 0,
  pendingUpdates: new Set(),

  resetStore: () => {
    set({
      patients: [],
      isLoading: false,
      error: null,
      lastUpdated: 0,
      pendingUpdates: new Set(),
    });
  },

  fetchPatients: async () => {
    set({ isLoading: true, error: null });
    try {
      const hospitalId = await getCurrentHospitalId();
      if (!hospitalId) {
        set({ patients: [], isLoading: false, error: null });
        return;
      }

      console.log('Fetching patients for hospital:', hospitalId);
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('hospital_id', hospitalId)
        .order('registered_at', { ascending: false });

      if (error) {
        console.error('Supabase error fetching patients:', error);
        throw error;
      }

      console.log('Raw database response:', data);
      console.log('Sample patient data:', data?.[0]);

      // Map snake_case to camelCase
      // Convert age from integer to display format "XX years"
      const mappedPatients = (data || []).map(patient => ({
        id: patient.id,
        name: patient.name,
        age: typeof patient.age === 'number' ? `${patient.age} years` : patient.age, // Convert integer to display format
        gender: patient.gender,
        contactNumber: patient.contact_number,
        priority: patient.priority,
        reason: patient.reason,
        referralDoctor: patient.referral_doctor,
        location: patient.location,
        doctorId: patient.doctor_id,
        registeredAt: patient.registered_at,
        status: patient.status,
        amountPaid: patient.amount_paid,
        paymentMode: patient.payment_mode,
        paymentHistory: patient.payment_history,
        services: patient.services,
        followUpDate: patient.follow_up_date,
        followUpStatus: patient.follow_up_status || 'scheduled',
        notes: patient.notes,
        createdAt: patient.created_at,
        updatedAt: patient.updated_at,
        appointmentTime: patient.appointment_time,
        completedAt: patient.completed_at,
        completedBy: patient.completed_by
      }));

      console.log('Mapped patients:', mappedPatients);
      console.log('Sample mapped patient:', mappedPatients?.[0]);
      set({ patients: mappedPatients, isLoading: false, lastUpdated: Date.now() });
      await savePatientsCache(mappedPatients);
    } catch (error) {
      console.error('Error fetching patients:', error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  addPatient: async (patient) => {
    try {
      console.log('Adding new patient with optimistic update:', patient);
      const now = patient.registeredAt || new Date().toISOString();
      const currentPatients = get().patients;
      
      const hospitalId = await requireHospitalIdForWrite();
      const hospitalName = await getHospitalNameForPatientId(hospitalId);
      
      // Convert age to integer if it's a string (for database storage)
      const ageForDB = typeof patient.age === 'string' 
        ? parseInt(patient.age.replace(/\s*(years?|months?|weeks?).*/i, '')) || 0
        : patient.age;

      const triedIds: string[] = [];
      let patientId = await resolveNextPatientId(hospitalName, currentPatients, triedIds);
      
      // Create optimistic patient object (use string format for UI display)
      const optimisticPatient: Patient = {
        id: patientId,
        name: patient.name,
        age: typeof ageForDB === 'number' ? `${ageForDB} years` : patient.age, // Display format
        gender: patient.gender,
        contactNumber: patient.contactNumber,
        priority: patient.priority || 'normal',
        reason: patient.reason,
        referralDoctor: patient.referralDoctor,
        location: patient.location,
        doctorId: patient.doctorId,
        registeredAt: now,
        status: 'waiting' as PatientStatus,
        amountPaid: 0,
        paymentMode: 'cash' as PaymentMode,
        paymentHistory: [],
        services: [],
        followUpDate: null,
        notes: '',
        createdAt: now,
        updatedAt: now,
        appointmentTime: undefined,
        completedAt: undefined,
        completedBy: undefined
      };

      // Optimistic update - immediately add to UI
      set(state => {
        const updated = [optimisticPatient, ...state.patients];
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, optimisticPatient.id])
        };
      });

      // Note: Patient is created directly in database below, no need to track for sync

      console.log('🏥 Current hospital_id for patient:', hospitalId);

      const insertPayload = {
        name: patient.name,
        age: typeof ageForDB === 'number' ? ageForDB : 0,
        gender: patient.gender,
        contact_number: patient.contactNumber,
        priority: patient.priority || 'normal',
        reason: patient.reason,
        referral_doctor: patient.referralDoctor || null,
        location: patient.location,
        doctor_id: patient.doctorId,
        hospital_id: hospitalId,
        registered_at: now,
        status: 'waiting',
        amount_paid: 0,
        payment_mode: 'cash',
        payment_history: [],
        services: [],
        follow_up_date: null,
        notes: '',
        created_at: now,
        updated_at: now,
      };

      let data: Record<string, unknown>[] | null = null;
      let lastError: { code?: string; message?: string } | null = null;

      let rpcRow: Record<string, unknown> | null = null;
      try {
        rpcRow = await insertPatientViaRpc(hospitalId, hospitalName, insertPayload);
      } catch (rpcUnexpected) {
        console.warn('insert_queue_patient unexpected error:', formatSupabaseError(rpcUnexpected));
      }
      if (rpcRow) {
        data = [rpcRow];
        patientId = rpcRow.id as string;
        set((state) => ({
          patients: state.patients.map((p) =>
            p.id === optimisticPatient.id ? { ...p, id: patientId } : p
          ),
          pendingUpdates: new Set(
            [...state.pendingUpdates].filter((id) => id !== optimisticPatient.id).concat(patientId)
          ),
        }));
      }

      for (let attempt = 0; data === null && attempt < 50; attempt++) {
        const result = await supabase
          .from('patients')
          .insert([{ id: patientId, ...insertPayload }])
          .select();

        if (!result.error && result.data?.length) {
          data = result.data;
          break;
        }

        lastError = result.error;
        if (isUniqueViolation(result.error)) {
          triedIds.push(patientId);
          patientId = await resolveNextPatientId(
            hospitalName,
            get().patients,
            triedIds
          );
          set((state) => ({
            patients: state.patients.map((p) =>
              p.id === optimisticPatient.id ? { ...p, id: patientId } : p
            ),
            pendingUpdates: new Set(
              [...state.pendingUpdates].filter((id) => id !== optimisticPatient.id).concat(patientId)
            ),
          }));
          continue;
        }
        break;
      }

      if (!data) {
        const error = lastError ?? { message: 'Failed to create patient' };
        console.error('Supabase error:', formatSupabaseError(error), error);
        set((state) => {
          const updated = state.patients.filter(
            (p) => p.id !== optimisticPatient.id && p.id !== patientId
          );
          void savePatientsCache(updated);
          return {
            patients: updated,
            pendingUpdates: new Set(
              [...state.pendingUpdates].filter(
                (id) => id !== optimisticPatient.id && id !== patientId
              )
            ),
            error: error.message ?? 'Failed to create patient',
          };
        });
        throw new Error(formatSupabaseError(error));
      }

      const mappedPatient = mapRowToPatient(data[0] as Record<string, unknown>);

      set(state => {
        const updated = state.patients.map((p) =>
          p.id === optimisticPatient.id || p.id === patientId ? mappedPatient : p
        );
        void savePatientsCache(updated);
        return {
          patients: updated,
          pendingUpdates: new Set(
            [...state.pendingUpdates].filter(
              (id) => id !== optimisticPatient.id && id !== patientId
            )
          ),
        };
      });

      return mappedPatient;
    } catch (error) {
      const message = formatSupabaseError(error);
      console.error('Error adding patient:', message, error);
      set((state) => ({
        ...state,
        error: message,
        patients: state.patients.filter((p) => !state.pendingUpdates.has(p.id)),
        pendingUpdates: new Set(),
      }));
      throw new Error(message);
    }
  },

  updatePatient: async (patientId, updates) => {
    try {
      const currentPatient = get().patients.find(p => p.id === patientId);
      if (!currentPatient) throw new Error('Patient not found');

      // Create optimistic update
      const optimisticPatient = {
        ...currentPatient,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      // Optimistic update - immediately update UI
      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? optimisticPatient : p);
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, patientId]),
          lastUpdated: Date.now() // Force re-render
        };
      });

      // Prepare backend update object
      const backendUpdates: any = {};
      
      // Map camelCase to snake_case and handle null values properly
      if ('registeredAt' in updates) {
        backendUpdates['registered_at'] = updates.registeredAt;
      }
      if ('followUpStatus' in updates && updates.followUpStatus !== undefined) {
        backendUpdates['follow_up_status'] = updates.followUpStatus;
      }
      if ('followUpDate' in updates) {
        // Handle null explicitly - set to null if provided, otherwise omit
        backendUpdates['follow_up_date'] = updates.followUpDate === null ? null : updates.followUpDate;
      }
      if ('doctorId' in updates) {
        backendUpdates['doctor_id'] = updates.doctorId;
      }
      if ('contactNumber' in updates) {
        backendUpdates['contact_number'] = updates.contactNumber;
      }
      if ('referralDoctor' in updates) {
        backendUpdates['referral_doctor'] = updates.referralDoctor;
      }
      if ('amountPaid' in updates) {
        backendUpdates['amount_paid'] = updates.amountPaid;
      }
      if ('paymentMode' in updates) {
        backendUpdates['payment_mode'] = updates.paymentMode;
      }
      if ('paymentHistory' in updates) {
        backendUpdates['payment_history'] = updates.paymentHistory;
      }
      if ('appointmentTime' in updates) {
        backendUpdates['appointment_time'] = updates.appointmentTime;
      }
      if ('completedAt' in updates) {
        backendUpdates['completed_at'] = updates.completedAt;
      }
      if ('completedBy' in updates) {
        backendUpdates['completed_by'] = updates.completedBy;
      }
      
      // Copy any other fields that are already in snake_case
      Object.keys(updates).forEach(key => {
        if (!['registeredAt', 'followUpStatus', 'followUpDate', 'doctorId', 'contactNumber', 
              'referralDoctor', 'amountPaid', 'paymentMode', 'paymentHistory', 
              'appointmentTime', 'completedAt', 'completedBy'].includes(key)) {
          // If it's already snake_case or a direct field, include it
          if (key.includes('_') || ['name', 'age', 'gender', 'reason', 'status', 'priority', 'location', 'notes', 'services'].includes(key)) {
            backendUpdates[key] = updates[key];
          }
        }
      });

      // Track change for incremental sync
      trackChange({
        id: patientId,
        table: 'patients',
        operation: 'update',
        data: {
          ...backendUpdates,
          updated_at: optimisticPatient.updatedAt
        },
        originalData: {
          ...currentPatient
        }
      });

      // Backend sync (now handled by incremental sync)
      const { data, error } = await supabase
        .from('patients')
        .update({
          ...backendUpdates,
          updated_at: optimisticPatient.updatedAt
        })
        .eq('id', patientId)
        .select()
        .single();

      if (error) {
        console.error('Error updating patient:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.map(p => p.id === patientId ? currentPatient : p);
          void savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        priority: data.priority,
        reason: data.reason,
        referralDoctor: data.referral_doctor,
        location: data.location,
        doctorId: data.doctor_id,
        registeredAt: data.registered_at,
        status: data.status,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        paymentHistory: data.payment_history,
        services: data.services,
        followUpDate: data.follow_up_date,
        followUpStatus: data.follow_up_status || 'scheduled',
        notes: data.notes,
        appointmentTime: data.appointment_time,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        completedAt: data.completed_at,
        completedBy: data.completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? mappedPatient : p);
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId))
        };
      });
    } catch (error) {
      console.error('Error updating patient:', error);
      set({ error: (error as Error).message });
    }
  },

  updatePatientStatus: async (patientId, status, completedBy) => {
    try {
      const now = new Date().toISOString();
      const currentPatient = get().patients.find(p => p.id === patientId);
      if (!currentPatient) throw new Error('Patient not found');

      // Create optimistic update
      const optimisticPatient = {
        ...currentPatient,
        status,
        ...(status === 'completed' && { completedAt: now }),
        ...(completedBy && { completedBy }),
        updatedAt: now
      };

      // Optimistic update - immediately update UI
      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? optimisticPatient : p);
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, patientId]),
          lastUpdated: Date.now() // Force re-render
        };
      });

      // Track change for incremental sync
      trackChange({
        id: patientId,
        table: 'patients',
        operation: 'update',
        data: {
          status,
          ...(status === 'completed' && { completed_at: now }),
          ...(completedBy && { completed_by: completedBy }),
          updated_at: now
        },
        originalData: {
          status: currentPatient.status,
          completed_at: currentPatient.completedAt,
          completed_by: currentPatient.completedBy,
          updated_at: currentPatient.updatedAt
        }
      });

      // Backend sync (now handled by incremental sync)
      const { data, error } = await supabase
        .from('patients')
        .update({
          status,
          ...(status === 'completed' && { completed_at: now }),
          ...(completedBy && { completed_by: completedBy }),
          updated_at: now
        })
        .eq('id', patientId)
        .select()
        .single();

      if (error) {
        console.error('Error updating patient status:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.map(p => p.id === patientId ? currentPatient : p);
          void savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        priority: data.priority,
        reason: data.reason,
        referralDoctor: data.referral_doctor,
        location: data.location,
        doctorId: data.doctor_id,
        registeredAt: data.registered_at,
        status: data.status,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        paymentHistory: data.payment_history,
        services: data.services,
        followUpDate: data.follow_up_date,
        notes: data.notes,
        appointmentTime: data.appointment_time,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        completedAt: data.completed_at,
        completedBy: data.completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? mappedPatient : p);
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId))
        };
      });
    } catch (error) {
      console.error('Error updating patient status:', error);
      set({ error: (error as Error).message });
    }
  },

  updatePatientDoctor: async (patientId, doctorId) => {
    try {
      const currentPatient = get().patients.find(p => p.id === patientId);
      if (!currentPatient) throw new Error('Patient not found');

      // Create optimistic update
      const optimisticPatient = {
        ...currentPatient,
        doctorId,
        updatedAt: new Date().toISOString()
      };

      // Optimistic update - immediately update UI
      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? optimisticPatient : p);
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, patientId]),
          lastUpdated: Date.now() // Force re-render
        };
      });

      // Track change for incremental sync
      trackChange({
        id: patientId,
        table: 'patients',
        operation: 'update',
        data: {
          doctor_id: doctorId,
          updated_at: new Date().toISOString()
        },
        originalData: {
          doctor_id: currentPatient.doctorId,
          updated_at: currentPatient.updatedAt
        }
      });

      // Backend sync (now handled by incremental sync)
      const { data, error } = await supabase
        .from('patients')
        .update({ doctor_id: doctorId })
        .eq('id', patientId)
        .select()
        .single();

      if (error) {
        console.error('Error updating patient doctor:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.map(p => p.id === patientId ? currentPatient : p);
          void savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        priority: data.priority,
        reason: data.reason,
        referralDoctor: data.referral_doctor,
        location: data.location,
        doctorId: data.doctor_id,
        registeredAt: data.registered_at,
        status: data.status,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        paymentHistory: data.payment_history,
        services: data.services,
        followUpDate: data.follow_up_date,
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        completedAt: data.completed_at,
        completedBy: data.completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? mappedPatient : p);
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId))
        };
      });
    } catch (error) {
      console.error('Error updating patient doctor:', error);
      set({ error: (error as Error).message });
    }
  },

  updatePatientPayment: async (patientId, amount, paymentMode) => {
    try {
      const currentPatient = get().patients.find(p => p.id === patientId);
      if (!currentPatient) throw new Error('Patient not found');

      const now = new Date().toISOString();
      const newPaymentHistory = [
        ...(currentPatient.paymentHistory || []),
        {
          amount,
          mode: paymentMode,
          date: now
        }
      ];

      // Create optimistic update
      const optimisticPatient = {
        ...currentPatient,
        amountPaid: (currentPatient.amountPaid || 0) + amount,
        paymentMode,
        paymentHistory: newPaymentHistory,
        updatedAt: now
      };

      // Optimistic update - immediately update UI
      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? optimisticPatient : p);
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, patientId]),
          lastUpdated: Date.now() // Force re-render
        };
      });

      // Track change for incremental sync
      trackChange({
        id: patientId,
        table: 'patients',
        operation: 'update',
        data: {
          amount_paid: optimisticPatient.amountPaid,
          payment_mode: paymentMode,
          payment_history: newPaymentHistory,
          updated_at: now
        },
        originalData: {
          amount_paid: currentPatient.amountPaid,
          payment_mode: currentPatient.paymentMode,
          payment_history: currentPatient.paymentHistory,
          updated_at: currentPatient.updatedAt
        }
      });

      // Backend sync (now handled by incremental sync)
      const { data, error } = await supabase
        .from('patients')
        .update({
          amount_paid: optimisticPatient.amountPaid,
          payment_mode: paymentMode,
          payment_history: newPaymentHistory,
          updated_at: now
        })
        .eq('id', patientId)
        .select()
        .single();

      if (error) {
        console.error('Error updating patient payment:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.map(p => p.id === patientId ? currentPatient : p);
          void savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        priority: data.priority,
        reason: data.reason,
        referralDoctor: data.referral_doctor,
        location: data.location,
        doctorId: data.doctor_id,
        registeredAt: data.registered_at,
        status: data.status,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        paymentHistory: data.payment_history,
        services: data.services,
        followUpDate: data.follow_up_date,
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        completedAt: data.completed_at,
        completedBy: data.completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? mappedPatient : p);
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId))
        };
      });
    } catch (error) {
      console.error('Error updating patient payment:', error);
      set({ error: (error as Error).message });
    }
  },

  addPatientServices: async (patientId, services) => {
    set({ isLoading: true, error: null });
    try {
      const patient = get().patients.find(p => p.id === patientId);
      if (!patient) throw new Error('Patient not found');

      // Format the services to match PatientService interface
      const formattedServices = services.map(service => ({
        id: uuidv4(),
        service_id: service.id,
        patient_id: patientId,
        service_name: service.name,
        price: service.price,
        added_at: new Date().toISOString()
      }));

      // Create optimistic update - immediately update UI
      const optimisticPatient = {
        ...patient,
        services: [...(patient.services || []), ...formattedServices],
        updatedAt: new Date().toISOString()
      };

      // Optimistic update - immediately update UI
      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? optimisticPatient : p);
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, patientId]),
          lastUpdated: Date.now() // Force re-render
        };
      });

      // Track change for incremental sync
      trackChange({
        id: patientId,
        table: 'patients',
        operation: 'update',
        data: {
          services: optimisticPatient.services,
          updated_at: optimisticPatient.updatedAt
        },
        originalData: {
          services: patient.services,
          updated_at: patient.updatedAt
        }
      });

      // Backend sync (now handled by incremental sync)
      const { data, error } = await supabase
        .from('patients')
        .update({
          services: optimisticPatient.services,
          updated_at: optimisticPatient.updatedAt
        })
        .eq('id', patientId)
        .select()
        .single();

      if (error) {
        console.error('Error adding patient services:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.map(p => p.id === patientId ? patient : p);
          void savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        priority: data.priority,
        reason: data.reason,
        referralDoctor: data.referral_doctor,
        location: data.location,
        doctorId: data.doctor_id,
        registeredAt: data.registered_at,
        status: data.status,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        paymentHistory: data.payment_history,
        services: data.services,
        followUpDate: data.follow_up_date,
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        appointmentTime: data.appointment_time,
        completedAt: data.completed_at,
        completedBy: data.completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? mappedPatient : p);
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId))
        };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      throw error;
    }
  },

  removePatientService: async (patientId, serviceId) => {
    try {
      const currentPatient = get().patients.find(p => p.id === patientId);
      if (!currentPatient) throw new Error('Patient not found');

      const serviceToRemove = currentPatient.services.find(s => s.id === serviceId);
      if (!serviceToRemove) throw new Error('Service not found');

      // Create optimistic update
      const optimisticPatient = {
        ...currentPatient,
        services: currentPatient.services.filter(s => s.id !== serviceId),
        updatedAt: new Date().toISOString()
      };

      // Optimistic update - immediately update UI
      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? optimisticPatient : p);
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, patientId]),
          lastUpdated: Date.now() // Force re-render
        };
      });

      // Track change for incremental sync
      trackChange({
        id: patientId,
        table: 'patients',
        operation: 'update',
        data: {
          services: optimisticPatient.services,
          updated_at: optimisticPatient.updatedAt
        },
        originalData: {
          services: currentPatient.services,
          updated_at: currentPatient.updatedAt
        }
      });

      // Backend sync (now handled by incremental sync)
      const { data, error } = await supabase
        .from('patients')
        .update({ 
          services: optimisticPatient.services,
          updated_at: optimisticPatient.updatedAt
        })
        .eq('id', patientId)
        .select()
        .single();

      if (error) {
        console.error('Error removing patient service:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.map(p => p.id === patientId ? currentPatient : p);
          void savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        priority: data.priority,
        reason: data.reason,
        referralDoctor: data.referral_doctor,
        location: data.location,
        doctorId: data.doctor_id,
        registeredAt: data.registered_at,
        status: data.status,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        paymentHistory: data.payment_history,
        services: data.services,
        followUpDate: data.follow_up_date,
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        completedAt: data.completed_at,
        completedBy: data.completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? mappedPatient : p);
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId))
        };
      });

      console.log(`Service ${serviceToRemove.service_name} removed from patient ${currentPatient.name}`);
    } catch (error) {
      console.error('Error removing patient service:', error);
      set({ error: (error as Error).message });
      throw error;
    }
  },

  resetPatientSession: async (patientId) => {
    try {
      const currentPatient = get().patients.find(p => p.id === patientId);
      if (!currentPatient) throw new Error('Patient not found');

      const now = new Date().toISOString();
      
      // Create optimistic update - reset payment and services for new session
      // BUT PRESERVE payment history for future visits
      const optimisticPatient: Patient = {
        ...currentPatient,
        amountPaid: 0,
        paymentMode: 'cash' as PaymentMode,
        paymentHistory: currentPatient.paymentHistory || [], // PRESERVE payment history
        services: [],
        updatedAt: now
      };

      // Optimistic update - immediately update UI
      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? optimisticPatient : p);
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, patientId]),
          lastUpdated: Date.now() // Force re-render
        };
      });

      // Track change for incremental sync
      trackChange({
        id: patientId,
        table: 'patients',
        operation: 'update',
        data: {
          amount_paid: 0,
          payment_mode: 'cash',
          payment_history: currentPatient.paymentHistory || [], // PRESERVE payment history
          services: [],
          updated_at: now
        },
        originalData: {
          amount_paid: currentPatient.amountPaid,
          payment_mode: currentPatient.paymentMode,
          payment_history: currentPatient.paymentHistory,
          services: currentPatient.services,
          updated_at: currentPatient.updatedAt
        }
      });

      // Backend sync
      const { data, error } = await supabase
        .from('patients')
        .update({
          amount_paid: 0,
          payment_mode: 'cash',
          payment_history: currentPatient.paymentHistory || [], // PRESERVE payment history
          services: [],
          updated_at: now
        })
        .eq('id', patientId)
        .select()
        .single();

      if (error) {
        console.error('Error resetting patient session:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.map(p => p.id === patientId ? currentPatient : p);
          void savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        priority: data.priority,
        reason: data.reason,
        referralDoctor: data.referral_doctor,
        location: data.location,
        doctorId: data.doctor_id,
        registeredAt: data.registered_at,
        status: data.status,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        paymentHistory: data.payment_history,
        services: data.services,
        followUpDate: data.follow_up_date,
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        completedAt: data.completed_at,
        completedBy: data.completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? mappedPatient : p);
        void savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId))
        };
      });
    } catch (error) {
      console.error('Error resetting patient session:', error);
      set({ error: (error as Error).message });
    }
  },

  updatePatientFollowUp: async (patientId, followUpDate, notes) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('patients')
        .update({
          follow_up_date: followUpDate.toISOString(),
          notes: notes || ''
        })
        .eq('id', patientId)
        .select()
        .single();

      if (error) throw error;

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? data : p);
        void savePatientsCache(updated);
        return { patients: updated, isLoading: false };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  removePatient: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('patients')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => {
        const updated = state.patients.filter(p => p.id !== id);
        void savePatientsCache(updated);
        return { patients: updated, isLoading: false };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  getPatientsByStatus: (status) => {
    return get().patients.filter(p => p.status === status);
  },

  getPatientsByDoctor: (doctorId: string) => {
    return get().patients.filter(p => p.doctorId === doctorId);
  }
}));
